import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DateTime } from 'luxon'
import { useAuth0 } from '@auth0/auth0-react'
import ReportPage from '../../reportPage.v2'
import TableWidgetSeparated from '@modul-connect/shared/components/molecules/tableWidget/tableWidgetSeparated.v2'
import { timespanOptions } from '../../datePickers/timespanPickers/timespanPickers'
import { dateTimeToString } from '@modul-connect/shared/utils/dateTimeUtils'
import { convertToSelectedUnit, formatAddress } from '../../../../utils/unitConverter'
import {
  fetchOverweightReportSummaries,
  fetchOverweightReport,
  downloadWeightReportExcel
} from '../../../../state/actions/vehicles'
import { getOverweightLogsId } from '../../../../state/reducers/app'
import { log_columns, summaryColumns } from './log_columns'
import KpiRow from '@modul-connect/shared/components/molecules/kpiRow/kpiRow'
import theme from '@modul-connect/shared/theme'
import TopFiveDisplay from '../../batteryReport/batteryReportDetails/topFiveDisplay/topFiveDisplay'
import { useLocation, useNavigate } from 'react-router-dom'
import { GroupingOptionSelector } from '../../tollPassages/summary/groupingOptionSelector' 
import SimpleTable from '@modul-connect/shared/components/molecules/tableWidget/simpleTable' 

const weightString = (log) => {
  const formatOverweight = (isOverweight, ratio, overweightKg) => {
    if (isOverweight || ratio > 1) {
      return `${(ratio * 100).toFixed(0)}% (${convertToSelectedUnit(overweightKg, 0, 'weight')})`
    }
    return `${(ratio * 100).toFixed(0)}%`
  }

  return {
    gross: formatOverweight(
      log.hasOverweightGross,
      log.gross?.ratio ?? 0,
      (log.weightGross ?? 0) - (log.gross?.max ?? 0)
    ),
    front: formatOverweight(
      log.hasOverweightFront,
      log.front?.ratio ?? 0,
      (log.weightFront ?? 0) - (log.front?.max ?? 0)
    ),
    rear: formatOverweight(
      log.hasOverweightRear,
      log.rear?.ratio ?? 0,
      (log.weightRear ?? 0) - (log.rear?.max ?? 0)
    )
  }
}

const mapLogsToFlatObjects = (logs) => {
  if (!logs?.length) return []

  return logs.map((log, index) => {
    const weight_formatted = weightString(log)

    return {
      id: index,
      key: `${index}_${log.boxId}`,
      boxId: log.boxId,
      startTimeISO: log.startTime,
      endTimeISO: log.endTime,
      timestamp: log.startTime ? DateTime.fromISO(log.startTime) : null,
      timestamp_end: log.endTime ? DateTime.fromISO(log.endTime) : null,
      driver: log.driver,
      vehicle: log.vehicle,
      weight_gross: weight_formatted.gross ?? '- %',
      weight_front: weight_formatted.front ?? '- %',
      weight_rear: weight_formatted.rear ?? '- %',
      startAddress: formatAddress(log.startAddress),
      endAddress: log.stopAddress?.formattedAddress,
      endTimeString: log.endTime ? dateTimeToString(DateTime.fromISO(log.endTime), 'time-only') : 'ongoing',
      startTimeString: log.startTime ? dateTimeToString(DateTime.fromISO(log.startTime), 'time-only') : null,
      distance: (log.distance || log.distance === 0) ? convertToSelectedUnit(log.distance, 1, 'distance') : '-',
      // purpose: (log.purpose || '').toLowerCase() === 'work' ? 'Business' : 'Private',
      startMileage: (log.startMileage || log.startMileage === 0) ? convertToSelectedUnit(log.startMileage, 0, 'distance') : '-',
    }
  })
}

const prepareData = (data) => {
  if (!data?.length) return []

  let lastDay = null
  const toReturn = []

  data.forEach((log) => {
    const startDT = DateTime.fromISO(log.timestamp)
    if (startDT.day + '_' + startDT.month + '_' + startDT.year !== lastDay) {
      toReturn.push({ isDivider: true, text: startDT.setLocale('en').toFormat('yyyy • EEEE d LLLL') })
      lastDay = startDT.day + '_' + startDT.month + '_' + startDT.year
    }

    toReturn.push([
      { value1: log.startTimeString, 
        value2: log.endTimeString ?? 'ongoing', 
        startTimeISO: log.startTimeISO,
        endTimeISO: log.endTimeISO
      },
      { value: log.startAddress },
      { value: log.endAddress ?? 'ongoing' },
      { value: log.distance, sortValue: log.distance },
      { value: log.vehicle },
      // { value: log.purpose },
      { value: log.weight_gross },
      { value: log.weight_front },
      { value: log.weight_rear },
    ])
  })

  return toReturn
}

const prepareCollapsibleData = (payload) => {
  const logs = payload?.logs ?? []
  const flat = mapLogsToFlatObjects(logs)
  return prepareData(flat)
}

export const groupingOptions = {
  day: 'day',
  all: 'all'
}

const WeightReportSummaryV2 = ({
  overweight_report_v2,
  overweight_report,
  log_loading,
  loading,
  downloading,
  themes,
  fetchOverweightReportSummaries,
  fetchOverweightReport,
  downloadWeightReportExcel,
  startDate,
  endDate,
  onTimeframePicked,
  onStartDatePicked,
  onEndDatePicked,
  disabled,
  selected_organisations
}) => {

  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()

  const [groupBy, setGroupBy] = useState(
    useQuery().get('groupby') ?? groupingOptions.day
  ) 

  const [kpiData, setKpiData] = useState(null)

  const [allLogsTableData, setAllLogsTableData] = useState([])

  const isLoadingSummaries = log_loading?.fetchOverweightReportSummaries === 'loading'
  const loadingSummariesFailed = log_loading?.fetchOverweightReportSummaries === 'failed'

  const allLogId = getOverweightLogsId(null, startDate.toSeconds(), endDate.toSeconds()) 
  const isLoadingAllLogs = loading?.fetchWeightReport_v2 && loading?.fetchWeightReport_v2[allLogId] === 'loading' 
  const loadingAllLogsFailed = loading?.fetchWeightReport_v2 && loading?.fetchWeightReport_v2[allLogId] === 'failed' 



  const goToWeightLogDetailsPage = (index, id, row, parentId) => {
    let log = null
    if(parentId) {
      const dayDT = DateTime.fromISO(parentId)
  
      const startTime = row[0].value1
  
      const startTimeLog = DateTime.local(dayDT.year,dayDT.month, dayDT.day, parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1])).toUTC()
  
      const logs = overweight_report?.data?.logs
  
      log = logs.find(l => {
        const logDate = new Date(l.startTime)
        const targetDate = new Date(startTimeLog)
    
        return (
          logDate.getFullYear() === targetDate.getFullYear() &&
        logDate.getMonth() === targetDate.getMonth() &&
        logDate.getDate() === targetDate.getDate() &&
        logDate.getHours() === targetDate.getHours() &&
        logDate.getMinutes() === targetDate.getMinutes()
        )
      })
    }

    // if(!parentId) {
    //   const startTimeIso = row[0].startTimeISO
    //   const allLogsId = getOverweightLogsId(null, startDate.toSeconds(), endDate.toSeconds()) 
    //   const allLogs = overweight_report_v2?.[allLogsId]?.data?.logs
    //   log = allLogs.find(l => l.startTime === startTimeIso)

    //   if (!log) return
    // }

    const timestamp = DateTime.fromISO(log.startTime)
  
    if (!timestamp || !timestamp.isValid) {
      return
    }

    const summaryId = log.boxId

    navigate('/reports/reports_weight-reports/' + summaryId + '/' + log.startTime)

    
  }


  useEffect(() => {
    if (groupBy === groupingOptions.day) {
      getAccessTokenSilently().then((accessToken) => {
        fetchOverweightReport(accessToken, startDate, endDate, null)
        fetchOverweightReportSummaries(
          accessToken,
          startDate,
          endDate,
          false
        )
      })
    } else {
      getAccessTokenSilently().then((accessToken) => {
        fetchOverweightReport(accessToken, startDate, endDate, null)
      })
    }
  }, [startDate.toSeconds(), endDate.toSeconds(), selected_organisations]) 

  function useQuery() {
    const { search } = useLocation()
    return React.useMemo(() => new URLSearchParams(search), [search])
  }

  const changeGroupBy = (group) => {
    setGroupBy(group)
  }

  useEffect(() => {
    if (!overweight_report_v2 || groupBy !== groupingOptions.all)
      return

    const logId = getOverweightLogsId(null, startDate?.toSeconds(), endDate?.toSeconds())
    setAllLogsTableData(overweight_report_v2[logId]?.data?.logs ?? [])
  }, [overweight_report_v2, groupBy]) 

  const summaryTableData = useMemo(() => {
    const groups = overweight_report?.data?.groupedLogs ?? []
    if (!groups.length) return []

    const handleGroups = groups.map((s) => {
      const dayDT = DateTime.fromISO(s.day)
      const start = dayDT.startOf('day')

      const dayLogs = overweight_report.data?.logs?.filter(l => Date.parse(DateTime.fromISO(l.startTime).startOf('day')) === Date.parse(start))

      const collapsibleData = {
        count: dayLogs?.length,
        logs: dayLogs
      }
     
      return {
        id: s.day,
        day: dayDT.setLocale('en').toFormat('yyyy • EEEE d LLL'),
        count: s.count,
        isExpandable: true,
        onExpand: async () => {
        },
        collapsibleData: collapsibleData,
      }
    })

    handleGroups.onSort = (sort, sortOrder, items) => {
      if(sort === 'day') {
        const sorted = items.sort((d1, d2) => {
          const toCompare1 = d1['id'] ? d1['id'] : d1['id'] === '-' ? 0 : d1['id']
          const toCompare2 = d2['id'] ? d2['id'] : d2['id'] === '-' ? 0 : d2['id']
          return sortOrder === 'asc' ? (toCompare1 < toCompare2 ? -1 : 1 ) : (toCompare1 > toCompare2 ? -1 : 1)
        })
        return sorted
      }
      const defaultSorted = items.sort((d1, d2) => {
        const toCompare1 = d1[sort + '_sort'] ? d1[sort + '_sort'] : d1[sort] === '-' ? 0 : d1[sort]
        const toCompare2 = d2[sort + '_sort'] ? d2[sort + '_sort'] : d2[sort] === '-' ? 0 : d2[sort]
        return sortOrder === 'asc' ? (toCompare1 < toCompare2 ? -1 : 1 ) : (toCompare1 > toCompare2 ? -1 : 1)
      })
      return defaultSorted
    }
    return handleGroups
    
  }, [overweight_report])

  const onDownloadAll = useCallback(async () => {
    const token = await getAccessTokenSilently()
    downloadWeightReportExcel(token, startDate, endDate)
  }, [getAccessTokenSilently, downloadWeightReportExcel, startDate, endDate])



  useEffect(() => {
    setKpiData([
      {
        title: 'Trips with overweight',
        value: overweight_report?.data?.count ?? '-'
      },
      {
        title: 'Of all trips',
        value: overweight_report?.data ? toPercentage(overweight_report.data.ratio) : '-',
        unit: overweight_report?.data ? '%' : '',
        color: percentageColor(overweight_report?.data?.ratio ?? 0),
        width: 180
      },
      {
        title: 'Distance with overweight',
        value: overweight_report?.data?.distance ? convertToSelectedUnit(overweight_report.data.distance, 0, 'distance') : '-'
      },
      {
        title: 'Of distance driven',
        value: overweight_report?.data ? toPercentage(overweight_report.data.distanceRatio) : '-',
        unit: overweight_report?.data ? '%' : '',
        color: percentageColor(overweight_report?.data?.distanceRatio ?? 0)
      },
      {
        title: 'Hours of overweight',
        value: overweight_report?.data?.totalHours?.toFixed(0) ?? '-'
      }
    ])
  }, [overweight_report])

  const toPercentage = (ratio) => `${(100 * ratio).toFixed(1)}`
  const percentageColor = (ratio) => ratio > 0.9 ? theme.colors.error : ratio > 0.67 ? theme.colors.yellowText : theme.colors.text

  const totalHours = Math.max(0.0001, endDate.diff(startDate, 'hours').hours)
  const top_five_data = overweight_report.data?.byVehicle
    ? overweight_report.data.byVehicle.map(d => ({
      name: d.vehicle,
      percentage: Math.min(Number(((d.hours / totalHours) * 100).toFixed(2)), 100),
      additionalInfo: 'Number of warnings: ' + d.trips,
      vehicleId: d.id
    }))
    : []

  const allLogsRows = prepareData(mapLogsToFlatObjects(allLogsTableData ?? []))

  return (
    <ReportPage
      hasData={summaryTableData.length > 0}
      title="Weight Report"
      noDataMessage="There are no weight warnings for the selected time period."
      noSearch
      onStartDatePicked={onStartDatePicked}
      onEndDatePicked={onEndDatePicked}
      timeSelectors={[timespanOptions.year, timespanOptions.month, timespanOptions.week, timespanOptions.day, timespanOptions.custom]}
      startDate={startDate}
      endDate={endDate}
      onTimeframePicked={(start, end) => onTimeframePicked(start, end)}
      isLoading={isLoadingAllLogs} 
      fetchingDataFailed={groupBy === groupingOptions.all ? loadingAllLogsFailed : loadingSummariesFailed} 

      kpi={kpiData ? <KpiRow graph title={'Summary'} width={'100%'} bgColor={'white'} data={kpiData} isLoading={isLoadingAllLogs} /> : null}
      disabled={disabled}
      isPreparingFile={downloading?.weight_report?.status === 'working'}
      downloadFailed={downloading?.weight_report?.status === 'failed'}
      onDownloadAll={onDownloadAll}
      reportGraph={top_five_data ?
        <TopFiveDisplay
          isLoading={isLoadingAllLogs}
          title={'Top 5 vehicles with overweight warnings'}
          unit={'%'}
          data={top_five_data}
          tooltipText={'Time spent with overweight'}
          onSelect={() => {
          }}
          onDeselect={() => {
          }}
        /> : null}

      groupingOptionSelector={
        <GroupingOptionSelector
          onChange={(selection) => { changeGroupBy(selection) }}
          options={groupingOptions}
          value={groupBy}
          disabled={groupBy === groupingOptions.all ? isLoadingAllLogs : isLoadingSummaries}
        />
      } 

      tables={
        groupBy === groupingOptions.day
          ? (!summaryTableData.length ? [] : ([
            <TableWidgetSeparated
              key="weights-summary"
              data={summaryTableData}
              defaultSort={'day'}
              pagination={true}
              loadingStatus={isLoadingSummaries ? 'loading' : undefined}
              totalItems={summaryTableData.length}
              columns={summaryColumns}
              themes={themes}
              isSortable
              collapsibleOnRowClick={goToWeightLogDetailsPage}
              showInnerSortArrows
              collapsibleDataLoadingStatus={(id) => {
                const logId = getOverweightLogsId(null, DateTime.fromISO(id).startOf('day').toSeconds(), DateTime.fromISO(id).endOf('day').toSeconds())
                return loading.fetchWeightReport_v2 && loading.fetchWeightReport_v2[logId]
              }}
              collapsibleLogColumns={log_columns}
              prepareCollapsibleData={prepareCollapsibleData}
            />
          ]))
          : (allLogsRows?.length
            ? ([
              <SimpleTable
                key="all-logs"
                data={allLogsRows}
                isLoading={isLoadingAllLogs}
                loadingFailed={loadingAllLogsFailed}
                columns={log_columns}
                showSortArrows
                totalItems={allLogsRows?.filter(r => !r.isDivider).length}
                pagination={true}
              />
            ])
            : [])
      } 
    />
  )
}

const mapStateToProps = (state) => ({
  overweight_report_v2: state.overweight_report_v2,
  overweight_report: state.overweight_report,
  log_loading: state.log_loading,
  loading: state.loading,
  downloading: state.downloading,
  themes: state.themes,
})

const mapDispatchToProps = (dispatch) => ({
  fetchOverweightReportSummaries: (token, start, end, desc) => dispatch(fetchOverweightReportSummaries(token, start, end, desc)),
  fetchOverweightReport: (token, start, end, boxId) => dispatch(fetchOverweightReport(token, start, end, boxId)),
  downloadWeightReportExcel: (token, start, end) => dispatch(downloadWeightReportExcel(token, start, end, true)),
})

export default connect(mapStateToProps, mapDispatchToProps)(WeightReportSummaryV2)